import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, CircleArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { products } from "../data/products";
import { LEGAL_NAME } from "../config/branding";
import copintarLogo from "../assets/copintar-logo-transparent.png";
import intdigifinLogo from "../assets/intdigifin-logo-transparent.png";
import vatoLogo from "../assets/vato-logo.png";
import kaiAvatar from "../assets/kai-avatar.png";
import jaiAvatar from "../assets/jai-avatar.png";

const capabilities = [
  {
    number: "01",
    title: "Intelligent operations",
    description:
      "We connect the work behind a business: customer conversations, staff workflows, information, reporting, and decisions.",
  },
  {
    number: "02",
    title: "Digital products with a job to do",
    description:
      "We build platforms that remove friction from the real world, not just interfaces that look impressive in a presentation.",
  },
  {
    number: "03",
    title: "Capability that stays with you",
    description:
      "Through practical programs, technical support, and clear implementation, teams gain confidence as their systems grow.",
  },
];

const signals = ["AI systems", "Product engineering", "Automation", "Technical enablement"];

const heroSignals = [
  { product: "Space-m8", label: "Property intelligence", metric: "87%", metricLabel: "requests resolved in one workspace", detail: "Tenant requests \u2192 team assignment \u2192 live resolution", accent: "#67b9a5", status: "Spaces in sync" },
  { product: "ImpactBase", label: "NGO operations", metric: "4/4", metricLabel: "approval stages visible", detail: "Budget check \u2192 due diligence \u2192 approval trail", accent: "#5274c8", status: "Compliance active" },
  { product: "VATO", label: "Learning systems", metric: "12", metricLabel: "practical learning paths", detail: "Challenge \u2192 learning path \u2192 applied capability", accent: "#d6e85c", status: "Pathway ready" },
  { product: "KAI + JAI", label: "From clarity to delivery", metric: "2", metricLabel: "companion perspectives", detail: "Complex challenge \u2192 clear opportunity \u2192 working solution", accent: "#d99d5b", status: "Building together" },
];

function HeroSignalConsole() {
  const shouldReduceMotion = useReducedMotion();
  const [activeSignal, setActiveSignal] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion) return undefined;
    const timer = window.setInterval(() => {
      setActiveSignal((current) => (current + 1) % heroSignals.length);
    }, 4300);
    return () => window.clearInterval(timer);
  }, [shouldReduceMotion]);

  const signal = heroSignals[activeSignal];

  return (
    <div className="relative mx-auto w-full max-w-xl lg:justify-self-end">
      <motion.div
        animate={shouldReduceMotion ? undefined : { y: [0, -9, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        className="relative overflow-hidden rounded-[2rem] border border-[#111712]/20 bg-[#171c18] p-3 shadow-[0_26px_60px_rgba(17,23,18,0.22)]"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_15%,rgba(214,232,92,0.22),transparent_25%),radial-gradient(circle_at_10%_100%,rgba(103,185,165,0.2),transparent_32%)]" />
        <div className="relative overflow-hidden rounded-[1.45rem] border border-white/10 bg-[#0e1210] p-5 text-white sm:p-6">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.19em] text-white/55">
              <span className="h-2 w-2 rounded-full bg-[#d6e85c] shadow-[0_0_0_4px_rgba(214,232,92,0.15)]" />
              Jisedai live systems
            </div>
            <div className="flex gap-1.5">
              {heroSignals.map((item, index) => <button key={item.product} type="button" aria-label={`Show ${item.product}`} onClick={() => setActiveSignal(index)} className={`h-1.5 rounded-full transition ${index === activeSignal ? "w-7 bg-[#d6e85c]" : "w-1.5 bg-white/25 hover:bg-white/60"}`} />)}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={signal.product}
              initial={shouldReduceMotion ? false : { opacity: 0, y: 14, filter: "blur(5px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={shouldReduceMotion ? undefined : { opacity: 0, y: -10, filter: "blur(4px)" }}
              transition={{ duration: 0.42, ease: "easeOut" }}
              className="pt-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div><p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{signal.label}</p><h2 className="mt-2 font-serif text-3xl tracking-[-0.04em] text-white sm:text-4xl">{signal.product}</h2></div>
                <span className="rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: signal.accent, backgroundColor: `${signal.accent}22`, border: `1px solid ${signal.accent}55` }}>{signal.status}</span>
              </div>
              <div className="mt-7 grid grid-cols-[0.8fr_1.2fr] gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"><p className="font-serif text-4xl tracking-[-0.05em]" style={{ color: signal.accent }}>{signal.metric}</p><p className="mt-2 text-xs leading-5 text-white/55">{signal.metricLabel}</p></div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"><p className="text-[10px] font-bold uppercase tracking-[0.17em] text-white/45">Workflow signal</p><div className="mt-4 flex items-end gap-2">{[42, 68, 49, 82, 63, 92].map((height, index) => <motion.span key={index} animate={shouldReduceMotion ? undefined : { height: [`${Math.max(24, height - 22)}%`, `${height}%`, `${Math.max(24, height - 10)}%`] }} transition={{ duration: 1.7, repeat: Infinity, repeatType: "mirror", delay: index * 0.12 }} className="block flex-1 rounded-t-sm" style={{ height: `${height}%`, backgroundColor: index === 5 ? signal.accent : "rgba(255,255,255,0.23)" }} />)}</div></div>
              </div>
              <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4"><p className="text-xs leading-6 text-white/75">{signal.detail}</p><div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10"><motion.div key={signal.product} initial={{ width: "12%" }} animate={{ width: "88%" }} transition={{ duration: 2.7, ease: "easeOut" }} className="h-full rounded-full" style={{ backgroundColor: signal.accent }} /></div></div>
            </motion.div>
          </AnimatePresence>
          <img src={kaiAvatar} alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-12 right-20 h-36 w-auto opacity-35" />
          <img src={jaiAvatar} alt="" aria-hidden="true" className="pointer-events-none -bottom-10 -right-4 h-32 w-auto opacity-35" />
        </div>
      </motion.div>
      <div className="absolute -bottom-5 -left-4 rounded-2xl border border-[#111712]/15 bg-[#f5f5ef] px-4 py-3 shadow-lg shadow-[#45695a]/10"><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#45695a]">Always building</p><p className="mt-1 text-xs text-[#4d5951]">From insight to useful systems</p></div>
    </div>
  );
}

export default function IntelligenceLanding() {
  return (
    <main className="overflow-hidden bg-[#eeeee8] text-[#111712]">
      <section id="about" className="relative min-h-[92vh] border-b border-[#111712]/15 px-5 pb-12 pt-28 sm:px-8 md:px-12 md:pt-36">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -right-[18rem] top-[-12rem] h-[44rem] w-[44rem] rounded-full border border-[#111712]/20" />
          <div className="absolute -right-[7rem] top-[-1rem] h-[28rem] w-[28rem] rounded-full border border-[#111712]/20" />
          <div className="absolute right-[10%] top-[19%] h-4 w-4 rounded-full bg-[#d6e85c] shadow-[0_0_0_10px_rgba(214,232,92,0.24)]" />
          <div className="absolute bottom-0 left-[18%] h-32 w-px bg-[#111712]/20" />
        </div>

        <div className="relative mx-auto flex min-h-[72vh] max-w-7xl flex-col justify-between">
          <div className="grid gap-14 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
            <div>
              <p className="mb-8 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.24em] text-[#3b5145]">
                <span className="h-2 w-2 rounded-full bg-[#b9d400]" />
                Jisedai / Foundry Africa
              </p>
              <h1 className="max-w-5xl font-serif text-[3.2rem] leading-[0.91] tracking-[-0.065em] text-[#111712] sm:text-6xl md:text-8xl lg:text-[6.8rem]">
                Technology that
                <span className="block text-[#45695a]">moves real work</span>
                <span className="relative inline-block">forward.<span aria-hidden="true" className="absolute -bottom-2 left-[6%] h-[3px] w-[88%] bg-[#d6e85c]" /></span>
              </h1>
              <p className="mt-9 max-w-xl text-lg leading-8 text-[#344039] md:text-xl md:leading-9">
                Jisedai designs the systems, products, and practical intelligence
                that help African organisations operate with more clarity and reach.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#contact" className="group inline-flex items-center gap-4 rounded-full bg-[#111712] px-6 py-3.5 text-sm font-bold text-[#f4f4ee] transition hover:bg-[#45695a]">
                  Start a conversation <ArrowUpRight size={17} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a href="#projects" className="inline-flex items-center gap-3 rounded-full border border-[#111712]/30 px-6 py-3.5 text-sm font-bold transition hover:border-[#111712] hover:bg-white/40">
                  Explore our products <ArrowDownRight size={17} />
                </a>
              </div>
            </div>
            <HeroSignalConsole />
          </div>

          <div className="mt-16 grid gap-8 border-t border-[#111712]/15 pt-6 md:grid-cols-[1fr_auto] md:items-end">
            <p className="max-w-xl text-sm leading-6 text-[#576259]">
              {LEGAL_NAME} is an engineering and intelligence partner for teams building what comes next.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-bold uppercase tracking-[0.17em] text-[#3b5145]">
              {signals.map((signal) => <span key={signal}>{signal}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="border-b border-[#111712]/15 px-5 py-24 sm:px-8 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">What we make possible</p>
            <h2 className="max-w-4xl font-serif text-4xl leading-[0.98] tracking-[-0.055em] text-[#111712] md:text-6xl">
              Intelligence should feel useful before it feels impressive.
            </h2>
          </div>
          <div className="mt-20 border-t border-[#111712]/15">
            {capabilities.map((capability) => (
              <article key={capability.number} className="grid gap-5 border-b border-[#111712]/15 py-8 md:grid-cols-[0.16fr_0.8fr_1fr] md:items-start md:py-10">
                <p className="text-sm font-bold text-[#45695a]">{capability.number}</p>
                <h3 className="font-serif text-3xl tracking-[-0.04em] md:text-4xl">{capability.title}</h3>
                <p className="max-w-xl text-base leading-7 text-[#4d5951]">{capability.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#d6e85c] px-5 py-24 sm:px-8 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 border-b border-[#111712]/20 pb-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">Meet the companions</p>
            <h2 className="max-w-4xl font-serif text-4xl leading-[0.95] tracking-[-0.055em] text-[#111712] md:text-6xl">
              From the question in front of you to the system that makes it work.
            </h2>
          </div>

          <div className="grid gap-5 pt-8 md:grid-cols-2">
            <article className="relative min-h-[34rem] overflow-hidden rounded-[2rem] bg-[#f5f5ef] p-7 md:min-h-[39rem] md:p-10">
              <div className="relative z-10 ml-auto max-w-[58%] md:max-w-[52%]">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#45695a]">KAI / clarity</p>
                <h3 className="mt-4 font-serif text-4xl leading-[0.94] tracking-[-0.05em] text-[#111712] md:text-5xl">Complex challenges, made clear.</h3>
                <p className="mt-5 text-base leading-7 text-[#4d5951]">KAI helps turn complex challenges into clear opportunities, giving teams a better place to begin.</p>
              </div>
              <div className="absolute bottom-0 left-0 h-[78%] w-[64%] bg-[radial-gradient(circle_at_40%_36%,rgba(191,212,196,0.9),transparent_52%)]" />
              <img src={kaiAvatar} alt="KAI, a Jisedai companion" className="absolute bottom-0 left-2 z-10 h-[78%] w-auto object-contain object-bottom md:left-7" />
              <span className="absolute bottom-7 right-7 z-20 h-3 w-3 rounded-full bg-[#d6e85c] shadow-[0_0_0_7px_rgba(214,232,92,0.35)] md:bottom-10 md:right-10" />
            </article>

            <article className="relative min-h-[34rem] overflow-hidden rounded-[2rem] bg-[#45695a] p-7 text-white md:min-h-[39rem] md:p-10">
              <div className="relative z-10 ml-auto max-w-[58%] md:max-w-[52%]">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d6e85c]">JAI / delivery</p>
                <h3 className="mt-4 font-serif text-4xl leading-[0.94] tracking-[-0.05em] md:text-5xl">Smart ideas, put to work.</h3>
                <p className="mt-5 text-base leading-7 text-white/75">JAI turns smart ideas into working solutions, helping move from possibility into practical progress.</p>
              </div>
              <div className="absolute bottom-0 left-0 h-[78%] w-[68%] bg-[radial-gradient(circle_at_40%_38%,rgba(214,232,92,0.35),transparent_54%)]" />
              <img src={jaiAvatar} alt="JAI, a Jisedai companion pointing to the delivery message" className="absolute bottom-0 -left-2 z-10 h-[77%] w-auto object-contain object-bottom md:left-0" />
              <span className="absolute bottom-7 right-7 z-20 h-3 w-3 rounded-full bg-[#d6e85c] shadow-[0_0_0_7px_rgba(214,232,92,0.25)] md:bottom-10 md:right-10" />
            </article>
          </div>
        </div>
      </section>

      <section id="projects" className="bg-[#111712] px-5 py-24 text-[#f4f4ee] sm:px-8 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-8 border-b border-white/15 pb-12 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#d6e85c]">The JisedAI ecosystem</p>
              <h2 className="mt-5 max-w-3xl font-serif text-4xl leading-[0.95] tracking-[-0.055em] md:text-6xl">
                Products built for the work that matters.
              </h2>
            </div>
            <p className="max-w-md text-lg leading-7 text-white/65">
              Each platform begins with a real operational problem and grows into a system that teams can trust every day.
            </p>
          </div>

          <div className="grid border-l border-white/15 sm:grid-cols-2 xl:grid-cols-4">
            {products.map((product, index) => (
              <Link key={product.slug} to={`/products/${product.slug}`} className="group min-h-[31rem] border-b border-r border-white/15 p-6 transition hover:bg-white/[0.06] md:p-7">
                <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.18em] text-[#d6e85c]">
                  <span>0{index + 1}</span><ArrowUpRight size={17} className="transition group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
                <div className={`mt-10 flex h-28 items-center justify-center rounded-2xl border border-white/10 px-5 ${product.logoSurface === "light" ? "bg-[#f5f5ef]" : "bg-white/[0.04]"}`}>
                  <img src={product.logo} alt={product.logoAlt} className={`max-h-16 max-w-[88%] object-contain ${product.logoClassName || ""}`} />
                </div>
                <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.17em] text-white/45">{product.category}</p>
                <h3 className="mt-4 font-serif text-3xl tracking-[-0.04em] text-white">{product.title}</h3>
                <p className="mt-4 text-sm leading-6 text-white/65">{product.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="programs" className="px-5 py-24 sm:px-8 md:px-12 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">Programs, events, and thinking</p>
            <h2 className="mt-5 max-w-4xl font-serif text-4xl leading-[0.95] tracking-[-0.055em] md:text-6xl">
              Better technology is built in the open.
            </h2>
          </div>
          <p className="max-w-lg text-lg leading-8 text-[#4d5951]">
            We bring people into the work through workshops, public conversations, practical learning, and field notes from the systems we are building.
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-7xl gap-5 md:grid-cols-2">
          <Link to="/events" className="group min-h-[20rem] rounded-3xl bg-[#bfd4c4] p-8 transition hover:bg-[#adc9b4] md:p-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#45695a]">In person and online</p>
            <div className="mt-20 flex items-end justify-between gap-5">
              <div><h3 className="font-serif text-4xl tracking-[-0.05em]">Upcoming events</h3><p className="mt-3 max-w-sm text-[#405047]">Workshops, bootcamps, and conversations for people putting technology to work.</p></div>
              <CircleArrowRight size={34} className="shrink-0 transition group-hover:translate-x-1" />
            </div>
          </Link>
          <Link to="/blog" className="group min-h-[20rem] rounded-3xl bg-[#d6e85c] p-8 transition hover:bg-[#cce141] md:p-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#405047]">From the foundry</p>
            <div className="mt-20 flex items-end justify-between gap-5">
              <div><h3 className="font-serif text-4xl tracking-[-0.05em]">Notes and perspectives</h3><p className="mt-3 max-w-sm text-[#405047]">Ideas, lessons, and practical reporting from the JisedAI ecosystem.</p></div>
              <CircleArrowRight size={34} className="shrink-0 transition group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      </section>

      <section className="border-y border-[#111712]/15 bg-[#e3e4db] px-5 py-16 sm:px-8 md:px-12">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 md:flex-row md:items-center md:justify-between">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">Technical partners</p><h2 className="mt-3 font-serif text-3xl tracking-[-0.04em] md:text-4xl">Built with specialist capability.</h2></div>
          <div className="flex flex-wrap items-center gap-5">
            <a href="https://www.copintar.com/" target="_blank" rel="noreferrer" className="flex h-20 w-48 items-center justify-center rounded-2xl border border-[#111712]/15 bg-white px-6 transition hover:-translate-y-1"><img src={copintarLogo} alt="Copintar" className="max-h-11 max-w-[85%] object-contain" /></a>
            <a href="https://www.intdigifin.com/" target="_blank" rel="noreferrer" className="flex h-20 w-48 items-center justify-center rounded-2xl bg-[#263190] px-6 transition hover:-translate-y-1"><img src={intdigifinLogo} alt="IntdiGiFin" className="max-h-10 max-w-[85%] object-contain" /></a>
            <a href="https://learn.jisedai.africa" target="_blank" rel="noopener noreferrer" className="flex h-20 w-48 items-center justify-center rounded-2xl border border-[#111712]/15 bg-[#f7f7f2] px-5 transition hover:-translate-y-1" aria-label="Visit VATO on Jisedai Learn">
              <img src={vatoLogo} alt="VATO logo" className="max-h-10 max-w-[88%] object-contain" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#45695a] px-5 py-24 text-[#f4f4ee] sm:px-8 md:px-12 md:py-32">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <div><p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#d6e85c]">Let’s make the next system useful</p><h2 className="mt-5 max-w-4xl font-serif text-5xl leading-[0.93] tracking-[-0.06em] md:text-7xl">Start with the work you need to move.</h2></div>
          <a href="#contact" className="inline-flex shrink-0 items-center gap-4 rounded-full bg-[#d6e85c] px-7 py-4 font-bold text-[#111712] transition hover:bg-white">Talk to JisedAI <ArrowUpRight size={18} /></a>
        </div>
      </section>
    </main>
  );
}
